<template>
  <div>
    <p class="text-2xl font-bold leading-tight">Default reminder method</p>
    <p class="text-sm">Select which method you want auto-selected when creating new reminders</p>
    <div class="mt-4">
      <button
        v-for="(method, i) in config.reminderMethods"
        :key="'reminder-methods-' + i"
        class="btn px-4 mr-1"
        :class=" val.defaultReminderMethod === method ? 'btn-purple' : 'border border-gray-700 light:border-gray-300'"
        @click="val.defaultReminderMethod = method"
      >{{ method }}</button>
    </div>


    <div class="flex w-full lg:w-1/2 mt-4 pt-4 border-t border-theme-200 light:border-theme-600">
      <ul class="flex flex-grow select-none">
        <li class="flex-1 mx-2">
          <a
            class="tab"
            :class="{ 'active': reminderTabs === 'e-mail' }"
            @click="selectReminderTab('e-mail')"
            >
            E-mail settings
          </a>
        </li>
        <li class="flex-1">
          <a
            class="tab"
            :class="{ 'active': reminderTabs === 'sms' }"
            @click="selectReminderTab('sms')"
            >
            SMS settings
          </a>
        </li>
      </ul>
    </div>
    <div
      class="border border-theme-200 light:border-theme-600 p-2 -mt-4 pt-8 rounded rounded-t-none"
      v-if="reminderTabs === 'e-mail'"
    >
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/3 px-2 mb-4">
          <label class="block text-xs font-bold mb-2" for="mail-host">
            Company Host <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="company host"
            v-slot="{ errors }"
          >
            <input
              name="mail-host"
              type="text"
              id="mail-host"
              class="form-control"
              v-model="val.mailHost"
              autocomplete="off"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full md:w-1/3 px-2 mb-4">
          <label class="block text-xs font-bold mb-2" for="mail-port">
            Port <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="port"
            v-slot="{ errors }"
          >
            <input
              name="mail-port"
              type="text"
              id="mail-port"
              class="form-control"
              v-model="val.mailPort"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full md:w-1/3 px-2 mb-4">
          <label class="block text-xs font-bold mb-2" for="mail-encryption">
            Encryption
          </label>
          <ValidationProvider
            rules=""
            name="encryption"
            v-slot="{ errors }"
          >
            <div class="relative">
              <select
                class="form-control pr-8"
                id="mail-encryption"
                v-model="val.mailEncryption"
              >
                <option value="">none</option>
                <option value="TLS">TLS</option>
                <option value="SSL">SSL</option>
                <option value="STARTTLS">STARTTLS</option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center text-xs px-2">
                <i class="fas fa-chevron-down"></i>
              </div>
            </div>
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>
      </div>

      <div class="flex flex-wrap items-end -mx-2">
        <div class="w-full md:w-1/3 px-2 mb-4">
          <label class="block text-xs font-bold mb-2" for="mail-username">
            Username <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="username"
            v-slot="{ errors }"
          >
            <input
              name="mail-username"
              type="text"
              id="mail-username"
              class="form-control"
              v-model="val.mailUsername"
              autocomplete="off"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full md:w-1/3 px-2 mb-4">
          <label class="block text-xs font-bold mb-2" for="mail-password">
            Password <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="password"
            v-slot="{ errors }"
          >
            <div class="flex items-center">
              <input
                name="mail-password"
                id="mail-password"
                :type="inputType.mailPassword"
                class="form-control form-no-outline h-10 border-r-0 rounded-r-none"
                v-model="val.mailPassword"
              >
              <button
                type="button"
                class="h-10 px-5 btn-blue rounded-r focus:outline-none"
                @click="toggleInputPasswordType('mailPassword')"
              >
                <i class="fas" :class="inputType.mailPassword === 'password' ? 'fa-eye' : 'fa-eye-slash'"></i>
              </button>
            </div>

            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full md:w-1/3 px-2 mb-4">
        <button type="button" class="btn btn-orange py-2 px-4" @click="testSMTP">
            <i class="fas fa-bolt"></i>
            <span class="ml-2">Test SMTP</span>
          </button>
        </div>
      </div>

      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/3 px-2 mb-4">
          <label class="block text-xs font-bold mb-2" for="mail-from-address">
            From address <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="from address"
            v-slot="{ errors }"
          >
            <input
              name="mail-from-address"
              type="text"
              id="mail-from-address"
              class="form-control"
              v-model="val.mailFromAddress"
              autocomplete="off"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full md:w-1/3 px-2 mb-4">
          <label class="block text-xs font-bold mb-2" for="main-from-name">
            From name <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="from name"
            v-slot="{ errors }"
          >
            <input
              name="main-from-name"
              type="text"
              id="main-from-name"
              class="form-control"
              v-model="val.mailFromName"
              autocomplete="off"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>
      </div>
    </div>
    <div
      class="border border-theme-200 light:border-theme-600 p-4 pt-12 -mt-4 rounded rounded-t-none"
      v-if="reminderTabs === 'sms'"
    >

      <div class="flex flex-wrap -mx-2">
        <div class="w-full lg:w-1/3 px-2">
          <div class="flex flex-wrap justify-between -mx-1">
            <p class="px-1 leading-none font-bold text-lg">Select SMS provider</p>
            <div class="px-1">
              <div class="flex">
                Check pricing
                <select class="form-control w-20 py-0 text-xs ml-1" v-model="pricing.country" @change="getPricing">
                  <option :value="country.iso_3166_2" v-for="country in Countries" :key="'country-' + country.id">{{ country.name }}</option>
                </select>
              </div>
            </div>
          </div>
          <table class="table table-select-reminder">
            <tbody>
              <tr
                :class="{ 'active': val.smsProvider === 'nexmo' }"
                @click="val.smsProvider = 'nexmo'"
              >
                <td>Nexmo</td>
                <td v-if="pricing.nexmo">{{ pricing.nexmo }} / sms</td>
              </tr>
              <tr
                :class="{ 'active': val.smsProvider === 'routee' }"
                @click="val.smsProvider = 'routee'"
              >
                <td>Routee</td>
                <td v-if="pricing.routee">{{ pricing.routee }} / sms</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="w-full lg:w-2/3 px-2 mt-4">

          <div v-if="val.smsProvider === 'nexmo'">
            <p class="text-xl font-bold text-pink-500 leading-tight">Nexmo</p>
            <a class="text-link" href="https://www.nexmo.com/" target="_blank">www.nexmo.com</a>

            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/3 px-2 mb-4">
                <label class="block text-xs font-bold mb-2" for="nexmo-key">
                  Nexmo key <span class="text-red-500">*</span>
                </label>
                <ValidationProvider
                  rules="required"
                  name="Nexmo key"
                  v-slot="{ errors }"
                >
                  <input
                    name="nexmo-key"
                    type="text"
                    id="nexmo-key"
                    class="form-control"
                    v-model="val.nexmoKey"
                    autocomplete="off"
                  >
                  <span class="text-sm text-red-500">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>

              <div class="w-full md:w-1/3 px-2 mb-4">
                <label class="block text-xs font-bold mb-2" for="nexmo-secret">
                  Nexmo secret <span class="text-red-500">*</span>
                </label>
                <ValidationProvider
                  rules="required"
                  name="nexmo secret"
                  v-slot="{ errors }"
                >
                  <div class="flex items-center">
                    <input
                      name="nexmo-secret"
                      :type="inputType.nexmoSecret"
                      id="nexmo-secret"
                      class="form-control form-no-outline h-10 border-r-0 rounded-r-none"
                      v-model="val.nexmoSecret"
                    >
                    <button
                      type="button"
                      class="h-10 px-5 btn-blue rounded-r focus:outline-none"
                      @click="toggleInputPasswordType('nexmoSecret')"
                    >
                      <i class="fas" :class="inputType.nexmoSecret === 'password' ? 'fa-eye' : 'fa-eye-slash'"></i>
                    </button>
                  </div>

                  <span class="text-sm text-red-500">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>

              <div class="w-full md:w-1/3 px-2 mb-4">
                <label class="block text-xs font-bold mb-2" for="nexmo-from-number">
                  Nexmo from number
                </label>
                <ValidationProvider
                  name="nexmo from number"
                  v-slot="{ errors }"
                >
                  <input
                    name="nexmo-from-number"
                    type="text"
                    id="nexmo-from-number"
                    class="form-control"
                    v-model="val.nexmoFromNumber"
                  >
                  <span class="text-sm text-red-500">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>

            <div class="mt-4" v-if="val.nexmoKey && val.nexmoSecret && !hasChanges">
              <p>Nexmo remaining balance: {{ balance.nexmo }}</p>
              <div class="btn btn-blue" @click="getNexmoBalance">Check balance</div>
            </div>

          </div>

          <div v-if="val.smsProvider === 'routee'">
            <p class="text-xl font-bold text-pink-500 leading-tight">
              Routee
              <span class="text-xs">
                <a class="text-link" href="https://www.routee.net/" target="_blank">(www.routee.net)</a>
              </span>
            </p>

            <p class="mt-4 text-sm" v-if="!val.routeeID || !val.routeeSecret">
              You can find your application ID and secret under "Applications" in your <a class="text-link" href="https://go.routee.net/#/management/applications" target="_blank">account dashboard</a>.
            </p>

            <div class="flex flex-wrap -mx-2 mt-4">
              <div class="w-full md:w-1/3 px-2 mb-4">
                <label class="block text-xs font-bold mb-2" for="routee-app-id">
                  Routee application ID <span class="text-red-500">*</span>
                </label>
                <ValidationProvider
                  rules="required"
                  name="routee application id"
                  v-slot="{ errors }"
                >
                  <input
                    name="routee-app-id"
                    type="text"
                    id="routee-app-id"
                    class="form-control"
                    v-model="val.routeeID"
                    autocomplete="off"
                  >
                  <span class="text-sm text-red-500">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>

              <div class="w-full md:w-1/3 px-2 mb-4">
                <label class="block text-xs font-bold mb-2" for="routee-app-secret">
                  Routee application secret <span class="text-red-500">*</span>
                </label>
                <ValidationProvider
                  rules="required"
                  name="routee application secret"
                  v-slot="{ errors }"
                >

                  <div class="flex items-center">
                    <input
                      name="routee-app-secret"
                      :type="inputType.routeeSecret"
                      id="routee-app-secret"
                      class="form-control form-no-outline h-10 border-r-0 rounded-r-none"
                      v-model="val.routeeSecret"
                    >
                    <button
                      type="button"
                      class="h-10 px-5 btn-blue rounded-r focus:outline-none"
                      @click="toggleInputPasswordType('routeeSecret')"
                    >
                      <i class="fas" :class="inputType.routeeSecret === 'password' ? 'fa-eye' : 'fa-eye-slash'"></i>
                    </button>
                  </div>

                  <span class="text-sm text-red-500">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>

              <div class="w-full md:w-1/3 px-2 mb-4">
                <label class="block text-xs font-bold mb-2" for="routee-from">
                  Routee from <span class="text-red-500">*</span>
                </label>
                <ValidationProvider
                  rules="required|alpha_num|max:11"
                  name="routee from"
                  v-slot="{ errors }"
                >
                  <input
                    name="routee-from"
                    type="text"
                    id="routee-from"
                    class="form-control"
                    v-model="val.routeeFrom"
                    maxlength="11"
                  >
                  <span class="text-sm text-red-500">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>

            <div class="mt-4" v-if="val.routeeID && val.routeeSecret && val.routeeFrom && !hasChanges">
              <p>Routee remaining balance: {{ balance.routee }}</p>
              <div class="btn btn-blue" @click="getRouteeBalance">Check balance</div>
            </div>

            <div class="text-sm border border-orange-500 rounded p-2 mt-4" v-if="!val.routeeID || !val.routeeSecret || !val.routeeFrom">
              <p>
                After you <a class="text-link" href="https://go.routee.net" target="_blank">sign up for a routee account</a>, we suggest you make your first deposit ASAP because you'll have to go through the standard KYC process, which can take a while depending which account type you choose. If you choose "company" you'll need to provide legal documents that "constitute your company's fiscal presence". After you first deposit they will send you an email to perform this.
              </p>
            </div>

          </div>

        </div>
      </div>


    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  props: {
    value: {},
    hasChanges: Boolean
  },
  data() {
    return {
      val: {...this.value},
      inputType: {
        mailPassword: 'password',
        routeeSecret: 'password',
        nexmoSecret: 'password',
      },
      reminderTabs: '',
      pricing: {
        country: '',
        nexmo: null,
        routee: null,
      },
      balance: {
        nexmo: null,
        routee: null,
      },
    }
  },
  watch: {
    'val.smsProvider'() {
      this.getBalanceForProvider()
    },
    val: {
      deep: true,
      handler(value) {
        this.$emit('input', value)
      }
    }
  },
  computed: {
    ...mapGetters({
      config: 'config/config',
      Countries: 'countries/countries',
    })
  },
  mounted() {
    this.getBalanceForProvider()
  },
  methods: {
    getBalanceForProvider() {
      if (this.val.smsProvider === 'routee') {
        this.getRouteeBalance()
      } else if (this.val.smsProvider === 'nexmo') {
        this.getNexmoBalance()
      }
    },
    getNexmoBalance() {
      if (!this.val.nexmoKey || !this.val.nexmoSecret) {
        return false
      }
      this.balance.nexmo = null
      axios.get('sms/nexmo/balance')
        .then(response => {
          this.balance.nexmo = response.data
        })
    },
    getRouteeBalance() {
      if (!this.val.routeeID || !this.val.routeeSecret) {
        return false
      }
      this.balance.routee = null
      axios.get('sms/routee/balance')
        .then(response => {
          this.balance.routee = response.data
        })
    },
    async getPricing() {
      if (!this.pricing.country || this.hasChanges) {
        return
      }
      let requests = []
      if (this.val.nexmoKey && this.val.nexmoSecret) {
        requests.push({name: 'nexmo', promise: axios.post('sms/nexmo/pricing', { country: this.pricing.country })})
      }
      if (this.val.routeeID && this.val.routeeSecret) {
        requests.push({name: 'routee', promise: axios.post('sms/routee/pricing', { country: this.pricing.country })})
      }
      if (!requests.length) {
        return
      }
      Promise.all(requests.map(r => r.promise))
        .then(values => {
          for (let i in requests) {
            this.pricing[requests[i].name] = values[i].data
          }
        })
    },
    selectReminderTab(name) {
      if (this.reminderTabs === name) {
        this.reminderTabs = ''
        return
      }
      this.reminderTabs = name
    },
    toggleInputPasswordType(name) {
      this.inputType[name] = this.inputType[name] === 'password' ? 'text' : 'password'
    },
    testSMTP() {
      axios.get('settings/check-smtp')
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'SMTP connection success',
            text: ''
          })
        })
        .catch(() => {
          this.$notify({
            type: 'error',
            title: 'SMTP connection error',
            text: ''
          })
        })
    },
  }
}
</script>
